import BackgroundGradient from '@nautilusbio/nb-design-system/assets/BackgroundGradient.webp';
import BackgroundGradientLight from '@nautilusbio/nb-design-system/assets/BackgroundGradientLight.webp';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import clsx from 'clsx';
import React, { type FC, type ReactNode, useLayoutEffect, useState } from 'react';

import styles from './styles.module.css';

interface LoginContainerProps {
  children: ReactNode;
}

const LoginContainer: FC<LoginContainerProps> = ({ children }) => {
  const [isBackgroundLoaded, setIsBackgroundLoaded] = useState(false);

  const backdropImageProps = {
    'aria-hidden': true as const,
    onError: () => setIsBackgroundLoaded(true),
    onLoad: () => setIsBackgroundLoaded(true),
  };

  useLayoutEffect(() => {
    // Blanks the app-shell background before paint so it doesn't flash through
    // while this component's own <img> fades in.
    const layoutShell = document.querySelector<HTMLElement>('#main-scrollable-area');
    const previousLayoutBackgroundImage = layoutShell?.style.backgroundImage;
    if (layoutShell) {
      layoutShell.style.backgroundImage = 'none';
    }

    return () => {
      if (layoutShell) {
        layoutShell.style.backgroundImage = previousLayoutBackgroundImage ?? '';
      }
    };
  }, []);

  return (
    <Stack
      alignItems="center"
      className={styles.container}
      fullHeight
      fullWidth
      justifyContent="center"
      orientation="horizontal"
    >
      {/* No alt text — identity announced via LoginForm's h1. Both theme variants
          always render; html[data-theme] CSS alone (styles.module.css) picks which
          is visible, no JS logic. */}
      <img
        {...backdropImageProps}
        alt=""
        className={clsx(styles.bgImage, styles.bgImageDark, isBackgroundLoaded && styles.bgImageLoaded)}
        data-testid="backdrop-dark"
        src={BackgroundGradient}
      />
      <img
        {...backdropImageProps}
        alt=""
        className={clsx(styles.bgImage, styles.bgImageLight, isBackgroundLoaded && styles.bgImageLoaded)}
        data-testid="backdrop-light"
        src={BackgroundGradientLight}
      />
      <div className={clsx(styles.formPanel, isBackgroundLoaded && styles.formPanelVisible)}>{children}</div>
    </Stack>
  );
};

export default LoginContainer;
